<template>
  <div>
    <div class="drawer-header pb-0">
      <span v-if="!this.isEdit">{{ $t('MDM_NEW') }} {{ $t(sectionTitle) }}</span>
      <span v-else>{{ this.data.name }}</span>
      <div class="header-action">
        <Button type="text" size="mini" @click="cancel">
          {{ $t('MDM_CANCEL') }}
        </Button>
        <Button type="primary" size="mini" @click="save">
          {{ isEdit ? $t('MDM_UPDATE') : $t('MDM_CREATE') }}
        </Button>
      </div>
    </div>

    <div class="drawer-body drawer-scroll px-20">
      <Form :inline="true" class="form-default form-multi">
        <span v-for="(item, index) in visibleFormConstants" :key="index">
          <span v-if="item.type === 'subColumn'">
            <h5 class="drawer-sub-title">
              {{ item.tlabel ? $t(item.tlabel) : item.title }}
            </h5>
            <FormItem
              v-for="(subItem, index) in item.subColumns"
              :key="index"
              :label="subItem.tlabel ? $t(subItem.tlabel) : subItem.title"
              :required="item.required">
              <Input
                :placeholder="`Enter ${
                  subItem.tlabel ? $t(subItem.tlabel) : subItem.title
                } here`"
                :type="subItem.inputType || 'text'"
                v-model="values[item.paramKey][subItem.paramKey]"
                @input="setValues($event, item.paramKey, subItem.paramKey)"
                :disabled="subItem.disabled || false">
              </Input>
            </FormItem>
          </span>

          <div v-else-if="item.type === 'break'" class="mt-10"></div>

          <h5 v-else-if="item.type === 'heading'" class="drawer-sub-title">
            {{ item.title }}
          </h5>

          <h5 v-else-if="item.type === 'switch'" class="drawer-sub-title">
            <span>{{ item.tlabel ? $t(item.tlabel) : item.title }}</span>
            <SwitchBtn
              v-model="values[item.paramKey]"
              label="right"
              size="small"
              :active-text="item.active_text"
              :inactive-text="item.inactive_text"
              @change="setValues($event, item.paramKey)"
              class="default-switch ml-auto w-75">
            </SwitchBtn>
          </h5>

          <FormItem
            v-else-if="item.type !== 'List'"
            :label="item.tlabel ? $t(item.tlabel) : item.title"
            :required="item.required">
            <Input
              v-if="item.type === 'input'"
              :placeholder="`Enter ${
                item.tlabel ? $t(item.tlabel) : item.title
              } here`"
              :type="'text'"
              v-model="values[item.paramKey]"
              @input="setValues($event, item.paramKey)"
              :disabled="item.disabled || false">
            </Input>
            <Select
              v-if="item.type === 'dropdown'"
              v-model="values[item.paramKey]"
              class="select-default no-bg"
              value-key="id"
              size="mini"
              filterable
              :disabled="item.disabled"
              placeholder="Select"
              @change="setValues($event, item.paramKey)">
              <Option
                v-for="option in item.options"
                :key="option[item.optionKey || 'id']"
                :label="option[item.optionLabel || 'name']"
                :value="option[item.optionKey || 'id']">
              </Option>
            </Select>

            <MultipleSelectDropdown
              v-else-if="item.type === 'MultiSelectList'"
              :options="item.options"
              :selected="values[item.paramKey]"
              :clearSelected="true"
              placeholder="Select options"
              :selectAllOption="false"
              :selectNameAndReference="true"
              label="ref_name"
              @updateOption="setValues($event, item.paramKey)" />
            <TimePicker
              v-else-if="item.type === 'timepicker'"
              :value="values[item.paramKey]"
              :placeholder="`Enter ${
                item.tlabel ? $t(item.tlabel) : item.title
              } here`"
              format="HH:mm:ss"
              value-format="HH:mm:ss"
              @input="setValues($event, item.paramKey)" />
          </FormItem>

          <div class="mt-20" v-if="item.endGroup"></div>

          <div v-if="item.type === 'checkbox'">
            <Checkbox
              v-for="(opt, index) in item.options"
              :label="opt.title"
              :key="index"
              :value="checkboxObj[item.paramKey][opt.title]"
              @change="radioSelected($event, item.paramKey, opt)">
              {{ opt.title }}
            </Checkbox>
          </div>

          <div
            class="mt-20"
            v-if="item.type === 'List' && checkIfSystemUser">
            <h5 class="drawer-sub-title">
              <span>{{ item.title }} - {{ selectedDepotsCount() }} selected</span>
            </h5>
            <div class="backdrop-overlay" v-if="showList"></div>
            <div class="position-relative" :class="{'is-focus' : showList}" v-click-outside="setShowList">
              <Input
                placeholder="Search"
                type="text"
                size="mini"
                class="input-search no-border z-index2"
                v-model="search"
                @focus="setFocus" />
              <div class="position-relative" v-if="showList">
                <Card
                  shadow="never"
                  class="card-default card-list-dropdown no-border">
                  <div
                    class="list-item list-boxed"
                    @click="selectAll(item.paramKey)"
                    :selected="isAllSelected">
                    <span class="icon-user icon-teal">
                      <svgicon name="icon-source" width="12" height="11" />
                    </span>
                    <div class="list-text">
                      <span>Select All Depots</span>
                    </div>
                  </div>
                  <div
                    class="list-item list-boxed"
                    @click="unSelectAll(item.paramKey)"
                    :selected="false" v-if="selectedDepotsCountVar > 0">
                    <span class="icon-user icon-teal">
                      <svgicon name="icon-source" width="12" height="11" />
                    </span>
                    <div class="list-text">
                      <span>UnSelect All Depots</span>
                    </div>
                  </div>
                  <div
                    class="list-item list-boxed"
                    :selected="listItem.selected"
                    v-for="(listItem, idx) in searchRender"
                    :key="idx"
                    @click="selectItem(listItem, item.paramKey)">
                    <span class="icon-user icon-teal">
                      <svgicon name="icon-source" width="12" height="11" />
                    </span>
                    <div class="list-text">
                      <span>{{ listItem.name }}</span>
                      <small class="pt-5">{{ listItem.reference_id }}</small>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
            <Card shadow="never" class="card-default card-list-scroll no-border">
              <div v-if="listRender">
                <div
                  v-for="(option, idx) in listRender"
                  :key="idx"
                  class="list-item list-boxed">
                  <span class="icon-user icon-teal">
                    <svgicon name="icon-source" width="12" height="11" />
                  </span>
                  <div class="list-text">
                    <span v-if="option.name">{{ option.name }}</span>
                    <small v-if="option[item.key]" class="pt-5">{{
                        option[item.key]
                      }}</small>
                  </div>
                  <span
                    class="icon-action pointer"
                    @click="deleteListItem(option, item)">
                    <svgicon name="icon-delete-md" width="12" height="12" />
                  </span>
                </div>
              </div>
            </Card>
          </div>
        </span>
      </Form>
    </div>
  </div>
</template>

<script>
import FilterDropdown from '@common/components/FilterDropdown';
import MultipleSelectDropdown from './MultipleDropdown';
import { systemRole } from '@/constants/mdm-v2/users';

export default {
  components: {
    FilterDropdown,
    MultipleSelectDropdown,
  },
  props: {
    formConstant: {
      type: Array,
    },
    sectionTitle: {
      type: String,
    },
    isEdit: {
      type: Boolean,
    },
    data: {
      type: Object,
    },
  },
  data() {
    return {
      values: {},
      freightForwaderKey: 'id',
      list: [],
      search: '',
      showList: false,
      isSelectedAll: false,
      isUnSelectAll: false,
      checkboxObj: {},
      selectedDepotsCountVar: 0,
      systemRole,
    };
  },
  created() {
    if (this.isEdit && this.data) {
      this.formConstant.forEach((val) => {
        if (val.paramKey && val.subColumns && val.subColumns.length) {
          if (val.type === 'subColumn') {
            this.values[val.paramKey] = {};
          }
          val.subColumns.forEach((subColumn) => {
            this.values[val.paramKey][subColumn.paramKey] =
              this.data[subColumn.paramKey] ||
              this.data[val.paramKey][subColumn.paramKey];
          });
        } else if (val.paramKey in this.data) {
          if (val.type === 'switch') {
            this.values[val.paramKey] = !!this.data[val.paramKey];
          } else if (val.type === 'dropdown') {
            this.values[val.paramKey] = this.data[val.paramKey] && this.data[val.paramKey].id;
          } else if (val.type === 'MultiSelectList') {
            this.values[val.paramKey] = this.data['hubRef'];
          } else {
            this.values[val.paramKey] = this.data[val.paramKey]; // filtering the required values needed for update
          }
        } else if (val.defaultValue) {
          this.values[val.paramKey] = val.defaultValue;
        }
      });
      if (this.values.depot_ids) {
        this.calculateSelectedDepotsCount(this.values.depot_ids);
      } else {
        this.calculateSelectedDepotsCount([]);
      }
    } else {
      this.formConstant.forEach((val) => {
        if (val.defaultValue) {
          this.values[val.paramKey] = val.defaultValue;
        }
        if (val.type === 'subColumn') {
          this.values[val.paramKey] = {};
        }
      });
      if (this.values.depot_ids) {
        this.calculateSelectedDepotsCount(this.values.depot_ids);
      } else {
        this.calculateSelectedDepotsCount([]);
      }
    }
  },
  computed: {
    visibleFormConstants() {
      return (
        this.formConstant && this.formConstant.filter((val) => !val.hideView)
      );
    },
    listRender() {
      const list = this.visibleFormConstants.find((val) => val.type === 'List');
      if (list) {
        const { paramKey } = list;
        return this.values[paramKey];
      }
      return null;
    },
    isAllSelected() {
      return this.isSelectedAll;
    },
    isUnSelectAllSelected() {
      return this.isUnSelectAll;
    },
    searchRender() {
      const list = this.visibleFormConstants.find((val) => val.type === 'List');
      if (list) {
        const { options, paramKey } = list;
        const listData =
          options && options.length
            ? options.map((opt) => {
              const listObj = {
                id: opt.id,
                name: opt.name,
                reference_id: opt.reference_id,
                selected: false,
              };
              if (
                this.values[paramKey] &&
                this.values[paramKey].length &&
                this.values[paramKey].find((val) => val._id === opt.id)
              ) {
                listObj.selected = true;
              }
              return listObj;
            })
            : [];
        if (this.search) {
          return listData.filter((val) => {
            if (val.name.toLowerCase().includes(this.search.toLowerCase()) || val.reference_id.toLowerCase().includes(this.search.toLowerCase())) {
              return val;
            }
          });
        }
        return listData.sort((a,b) => b.selected - a.selected);
      }
      return [];
    },
    checkIfSelected() {
      return false;
    },
    checkIfSystemUser() {
      if (this.values && this.values.role_id) {
        const role_id = this.values.role_id;
        const list = this.visibleFormConstants.find((val) => val.paramKey === 'role_id');
        if (list) {
          const { options } = list;
          let role = options.find((data) => data.id === role_id);
          if (role.name === systemRole) {
            return false;
          } else {
            return true;
          }
        }
      }
      return true;
    },
  },
  methods: {
    resetValue() {
      this.$emit('resetFormOptions');
    },
    setFocus() {
      this.showList = true;
    },
    cancel() {
      this.$emit('cancel');
      this.resetValue();
    },
    save() {
      const extractPayload = this.formConstant.filter(
        (constant) => !constant.ignore_field
      );
      const payloadDetails = {};
      extractPayload.forEach((constant) => {
        payloadDetails[constant.paramKey] = this.values[constant.paramKey];
        if (constant.defaultValue && !payloadDetails[constant.paramKey]) {
          payloadDetails[constant.paramKey] = constant.defaultValue;
        }
      });
      if (this.data && this.isEdit) {
        this.$emit('update', payloadDetails);
      } else {
        this.$emit('add', payloadDetails);
      }
    },
    setValues(data, paramKey, subParamKey = null) {
      if (subParamKey) {
        this.values[paramKey][subParamKey] = data;
      } else {
        this.values[paramKey] = data;
      }
      if (paramKey === this.freightForwaderKey) {
        const ffOptions = this.formConstant.find(
          (freightForwader) => freightForwader.paramKey === paramKey
        );
        const ffDetails = ffOptions.options.find(
          (freightForwader) => freightForwader.id === this.values[paramKey]
        );
        this.values.govt_id = ffDetails.govt_id;
        this.values.status = ffDetails.status || true;
        this.values.admin.admin_name = ffDetails.admin_name;
        this.values.admin.admin_email = ffDetails.admin_email;
        this.values.admin.admin_phone = ffDetails.admin_phone;
      }
      if (paramKey === 'depots') {
        this.$emit('setGateOptions', data);
      }
      this.values = Object.assign({}, this.values);
    },
    deleteListItem(option, formObj) {
      const allOptions = this.values[formObj.paramKey];
      this.values[formObj.paramKey] = allOptions.reduce((acc, opt) => {
        if (opt._id !== option._id) {
          acc.push(opt);
        }
        return acc;
      }, []);
      this.values = Object.assign({}, this.values);
      this.calculateSelectedDepotsCount(this.values[formObj.paramKey] || [])
    },
    setShowList() {
      this.showList = false;
    },
    selectItem(item, key) {
      if (!item.selected) {
        if (!this.values[key]) {
          this.values[key] = [];
        }
        this.values[key].push({
          _id: item.id,
          name: item.name,
        });
        this.values = Object.assign({}, this.values);
        this.isSelectedAll = this.values[key].every(
          item => item.selected === true,
        );
        this.isUnSelectAll = this.values[key].every(
          item => item.selected === false,
        );
      } else {
        this.values[key] = this.values[key].filter((obj) => obj._id != item.id);
        this.values = Object.assign({}, this.values);
      }
      this.calculateSelectedDepotsCount(this.values[key]);
    },
    selectAll(paramKey) {
      const list = this.visibleFormConstants.find((val) => val.type === 'List');
      if (list) {
        const { options } = list;
        const allData = options.map(opt => ({
          _id: opt.id,
          name: opt.name,
        }));
        this.values[paramKey] = allData;
        this.values = Object.assign({}, this.values);
        this.isSelectedAll = true;
        this.calculateSelectedDepotsCount(this.values[paramKey]);
      }
    },
    unSelectAll(paramKey) {
      const list = this.visibleFormConstants.find((val) => val.type === 'List');
      if (list) {
        this.values[paramKey] = [];
        this.values = Object.assign({}, this.values);
        this.isUnSelectAll = true;
        this.isSelectedAll = false;
        this.calculateSelectedDepotsCount([]);
      }
    },
    radioSelected(event, paramKey, subObj) {
      if (!this.checkboxObj[paramKey]) this.checkboxObj[paramKey] = {};
      this.checkboxObj[paramKey][subObj.title] = event;
      this.checkboxObj = Object.assign({}, this.checkboxObj);
    },
    selectedDepotsCount() {
      return this.selectedDepotsCountVar;
    },
    calculateSelectedDepotsCount(arr) {
      this.selectedDepotsCountVar = arr.length;
    },
  },
};
</script>
